import assign from 'lodash/assign';
import React, {Component} from 'react';

//helpers
const getDisplayName = (WrappedComponent: {displayName?: string; name?: string}, prefix: string) => {
  const {displayName, name} = WrappedComponent;
  return `${prefix}(${displayName || name || 'Component'})`;
};

type ClassComponentConstructor = new (...args: any[]) => React.Component;

/**
 * Adds className and style props onto the root element
 * to make a component styleable with the styled-components library.
 */
export function styleable<T extends ClassComponentConstructor>(WrappedComponent: T): T;
// The function-component overload stays `any` rather than generic `<P>` on purpose: a precise
// signature would propagate the real prop types of styleable-wrapped FCs (e.g. Spinner) to their
// call sites, surfacing latent prop errors that belong to the broader JS->TS migration (SG-6383),
// not this config consolidation. Class components above keep their precise typing.
export function styleable(WrappedComponent: React.FunctionComponent<any>): React.ComponentType<any>;
export function styleable(WrappedComponent: any): any {
  if (typeof WrappedComponent !== 'function') {
    return null;
  }
  const isClassComponent = WrappedComponent.prototype && WrappedComponent.prototype.isReactComponent;

  const ParentClass = isClassComponent ? WrappedComponent : Component;

  return class extends ParentClass {
    static displayName = getDisplayName(WrappedComponent, 'Styleable');

    render() {
      const originalElement = isClassComponent ? super.render() : WrappedComponent(this.props);

      if (!originalElement) {
        return originalElement;
      }

      const className = [originalElement.props.className, this.props.className].filter(Boolean).join(' ');

      const style = assign({}, originalElement.props.style, this.props.style);

      return React.cloneElement(originalElement, {...originalElement.props, className, style});
    }
  };
}
